// caiet UC-DASH-03/04 — the interval a dashboard viewer may select, per
// docs/shape-ops-dashboard-time-range.md §2. Three processes must agree on it:
// the analytics service validates a definition, the bff's Grafana gate refuses a
// panel query outside it, and the reading page offers only what passes. One copy
// here, because the three copies before it had their day counts written by hand.
//
// The codes are a closed vocabulary (the wireframe's INTERVALE / INTERVALE_MAX in
// wireframes/react/src/lib/admin-data.ts) so the viewer renders one translated
// label instead of arithmetic. Days are calendar days of Europe/Chisinau.

import { addCalendarDays } from './business-date';

export const DASHBOARD_DEFAULT_RANGES = [
  'last-7-days',
  'last-30-days',
  'last-90-days',
  'last-12-months',
] as const;

// Both limits use this vocabulary: `max_range` bounds the length of the interval,
// `max_lookback` how far before today it may start.
export const DASHBOARD_RANGE_LIMITS = [
  '3-months',
  '12-months',
  '24-months',
  '5-years',
  'unlimited',
] as const;

export type DashboardDefaultRange = (typeof DASHBOARD_DEFAULT_RANGES)[number];
export type DashboardRangeLimit = (typeof DASHBOARD_RANGE_LIMITS)[number];

// A preset ends today and starts that many days before. `now-12M` covers 365 or
// 366 days depending on the year, so it counts as its longer case.
const DEFAULT_RANGE_DAYS: Record<DashboardDefaultRange, number> = {
  'last-7-days': 7,
  'last-30-days': 30,
  'last-90-days': 90,
  'last-12-months': 366,
};

// The longest stretch N calendar months can cover, so a preset of that length
// passes the limit in every year (§2.2).
const RANGE_LIMIT_DAYS: Record<DashboardRangeLimit, number> = {
  '3-months': 92,
  '12-months': 366,
  '24-months': 731,
  '5-years': 1827,
  unlimited: Number.POSITIVE_INFINITY,
};

export function isDashboardDefaultRange(value: string): value is DashboardDefaultRange {
  return (DASHBOARD_DEFAULT_RANGES as readonly string[]).includes(value);
}

export function isDashboardRangeLimit(value: string): value is DashboardRangeLimit {
  return (DASHBOARD_RANGE_LIMITS as readonly string[]).includes(value);
}

export function defaultRangeDays(range: string): number {
  return isDashboardDefaultRange(range) ? DEFAULT_RANGE_DAYS[range] : 0;
}

// An unknown code bounds nothing beyond the dashboard's own default (see
// effectiveBounds): the gate fails closed on a value no form could have written.
export function rangeLimitDays(limit: string): number {
  return isDashboardRangeLimit(limit) ? RANGE_LIMIT_DAYS[limit] : 0;
}

export interface DashboardRangeSettings {
  defaultRange: string;
  maxRange: string;
  maxLookback: string;
}

export interface DashboardRangeBounds {
  // The most days from the first day of the interval to its last.
  maxDays: number;
  // The most days before today the interval may start.
  lookbackDays: number;
}

// The dashboard's own default is always admitted: only a row older than the
// "default fits the limits" check can carry one outside them, and refusing it
// would open a dashboard whose every panel fails (§2.2).
export function effectiveBounds(settings: DashboardRangeSettings): DashboardRangeBounds {
  const floor = defaultRangeDays(settings.defaultRange);
  return {
    maxDays: Math.max(rangeLimitDays(settings.maxRange), floor),
    lookbackDays: Math.max(rangeLimitDays(settings.maxLookback), floor),
  };
}

const CHISINAU_DATE = new Intl.DateTimeFormat('en-CA', {
  timeZone: 'Europe/Chisinau',
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
});

/** The calendar day an instant falls on in Chisinau, as `YYYY-MM-DD`. */
export function chisinauDate(epochMs: number): string {
  return CHISINAU_DATE.format(new Date(epochMs));
}

function dayNumber(isoDate: string): number {
  const [year, month, day] = isoDate.split('-').map(Number) as [number, number, number];
  return Math.round(Date.UTC(year, month - 1, day) / 86_400_000);
}

// Two `YYYY-MM-DD` dates apart, in whole days. Local: `calendarDaysBetween` in
// business-date.ts is the same count between two instants, and `addCalendarDays` is
// read from there.
function daysBetweenDates(fromIsoDate: string, toIsoDate: string): number {
  return dayNumber(toIsoDate) - dayNumber(fromIsoDate);
}

/** The first day an interval may start on, or null when the lookback is unlimited. */
export function earliestStartDate(bounds: DashboardRangeBounds, nowMs: number): string | null {
  if (!Number.isFinite(bounds.lookbackDays)) return null;
  return addCalendarDays(chisinauDate(nowMs), -bounds.lookbackDays);
}

export type DashboardRangeRefusal = 'order' | 'length' | 'lookback';

// Why an interval is outside the bounds, or null when it is inside them. Dates
// are Chisinau calendar days: today-366 ... today is 366 days long.
export function dashboardDateRangeRefusal(
  bounds: DashboardRangeBounds,
  fromIsoDate: string,
  toIsoDate: string,
  nowMs: number,
): DashboardRangeRefusal | null {
  const length = daysBetweenDates(fromIsoDate, toIsoDate);
  if (length < 0) return 'order';
  if (length > bounds.maxDays) return 'length';
  const earliest = earliestStartDate(bounds, nowMs);
  if (earliest !== null && fromIsoDate < earliest) return 'lookback';
  return null;
}

export function dashboardRangeRefusal(
  bounds: DashboardRangeBounds,
  fromMs: number,
  toMs: number,
  nowMs: number,
): DashboardRangeRefusal | null {
  return dashboardDateRangeRefusal(bounds, chisinauDate(fromMs), chisinauDate(toMs), nowMs);
}

// The UTC instant a Chisinau calendar day begins. The offset is read for the
// instant itself, then once more in case the first guess fell across a change
// of daylight saving time.
export function chisinauDayStartMs(isoDate: string): number {
  const utcMidnight = dayNumber(isoDate) * 86_400_000;
  const first = utcMidnight - chisinauOffsetMs(utcMidnight);
  return utcMidnight - chisinauOffsetMs(first);
}

const CHISINAU_CLOCK = new Intl.DateTimeFormat('en-US', {
  timeZone: 'Europe/Chisinau',
  hourCycle: 'h23',
  year: 'numeric',
  month: 'numeric',
  day: 'numeric',
  hour: 'numeric',
  minute: 'numeric',
  second: 'numeric',
});

function chisinauOffsetMs(epochMs: number): number {
  const parts: Record<string, number> = {};
  for (const part of CHISINAU_CLOCK.formatToParts(new Date(epochMs))) {
    if (part.type !== 'literal') parts[part.type] = Number(part.value);
  }
  const wall = Date.UTC(
    parts['year'] ?? 1970,
    (parts['month'] ?? 1) - 1,
    parts['day'] ?? 1,
    parts['hour'] ?? 0,
    parts['minute'] ?? 0,
    parts['second'] ?? 0,
  );
  return wall - Math.floor(epochMs / 1000) * 1000;
}
